@import 'styles/mixins';
@import 'styles/blaze/mixins';
@import 'styles/variables';

.root {
  transition: all 0.2s ease-in-out, visibility 0s;
  -webkit-transition: all 0.2s ease-in-out, visibility 0s;
  display: inline-flex;
  align-items: center;
  justify-content: center;
  white-space: nowrap;
  position: relative;

  &.fullWidth {
    width: 100%;
  }

  &.rounded {
    border-radius: 32px;
  }

  &.square {
    flex-direction: column;
    aspect-ratio: 1;
    width: auto;
    height: auto;
    min-width: 0;
    min-height: 0;
    align-items: center;
    justify-content: center;

    .children {
      white-space: normal;
      text-align: center;
      line-height: 1.2;
      display: flex;
      align-items: center;
      justify-content: center;
    }
  }

  &.vertical {
    flex-direction: column;
    align-items: center;
    justify-content: center;
    gap: 4px;
    padding: 12px 12px 8px 12px;

    [data-autopilot-system-redesign='on'] & {
      height: inherit;
    }

    .children {
      letter-spacing: 0.28px;
      line-height: 135%;
      display: flex;
      align-items: center;
      justify-content: center;
      padding: 0;
    }
  }

  &.disabled {
    opacity: 0.3;
    pointer-events: none;
  }

  .chevron {
    display: flex;
    align-items: center;
    justify-content: center;
    margin-left: auto;

    &.up {
      transform: rotate(180deg);
    }
  }

  svg {
    flex-shrink: 0;
  }

  .children {
    display: flex;
    min-width: 0;
    flex-wrap: wrap;
  }
}

// Sizes
.xs {
  @include xs-sohne-medium;
  line-height: 13px;
  gap: 2px;
  padding: 6px;
  border-radius: 4px;
  min-height: 28px;

  [data-autopilot-system-redesign='on'] & {
    @include sm-sohne;
    height: 24px;
    min-height: 24px;
    padding: 3px;
    border-radius: 6px;
    line-height: 107%;
    letter-spacing: 0.14px;

    &.vertical {
      min-height: 26px;
    }

    &.rounded {
      border-radius: 32px;
    }

    &.iconButton {
      width: 24px;
      height: 24px;
      min-height: 24px;
    }

    @include for-phone-only {
      height: 24px;
      min-height: 24px;

      &.iconButton {
        width: 24px;
        height: 24px;
        min-height: 24px;
      }
    }
  }

  &.iconButton {
    padding: 6px;
    width: 28px;
    height: 28px;
  }

  &.square {
    padding: 6px;
    min-height: 28px;
    min-width: 28px;
  }

  .children {
    padding: 0 2px;
  }
}

.sm {
  @include sm-sohne-medium;
  line-height: 16px;
  gap: 2px;
  padding: 6px 8px;
  border-radius: 6px;
  min-height: 32px;

  [data-autopilot-system-redesign='on'] & {
    @include sm-sohne;
    height: 28px;
    min-height: 28px;
    padding: 6px;
    line-height: 139%; /* 19.46px */
    letter-spacing: 0.14px;

    &.vertical {
      min-height: 32px;
    }

    &.iconButton {
      width: 28px;
      height: 28px;
      min-height: 28px;
    }

    .children {
      transform: none;
    }

    @include for-phone-only {
      height: 32px;
      min-height: 32px;

      &.iconButton {
        width: 32px;
        height: 32px;
        min-height: 32px;
      }
    }
  }

  &.iconButton {
    padding: 6px;
    width: 32px;
    height: 32px;
  }

  &.square {
    padding: 6px;
    min-height: 32px;
    min-width: 32px;
  }

  .children {
    padding: 0 2px;
  }
}

.md {
  @include sm-sohne-medium;
  line-height: 20px;
  gap: 2px;
  padding: 8px 8px 8px 8px;
  border-radius: 6px;
  min-height: 36px;

  [data-autopilot-system-redesign='on'] & {
    @include sm-sohne;
    height: 32px;
    min-height: 32px;
    padding: 8px;
    border-radius: 8px;
    line-height: 109%; /* 15.26px */

    &.vertical {
      min-height: 36px;
    }

    &.rounded {
      border-radius: 32px;
    }

    &.iconButton {
      width: 32px;
      height: 32px;
      min-height: 32px;
    }

    .children {
      padding: 0 3px;
      transform: none;
    }

    @include for-phone-only {
      @include md-sohne;
      height: 40px;
      min-height: 40px;

      &.iconButton {
        width: 40px;
        height: 40px;
        min-height: 40px;
      }
    }
  }

  &.iconButton {
    width: 36px;
    height: 36px;
  }

  &.square {
    padding: 8px;
    min-height: 36px;
    min-width: 36px;
  }

  .children {
    padding: 0 6px;

    img {
      transform: translateY(1px);
    }
  }
}

.lg {
  @include md-sohne-medium;
  line-height: 20px;
  gap: 2px;
  padding: 10px;
  border-radius: 8px;
  min-height: 40px;

  [data-autopilot-system-redesign='on'] & {
    @include md-sohne;
    height: 36px;
    min-height: 36px;
    padding: 9px;
    line-height: 96%; /* 15.36px */

    &.vertical {
      min-height: 40px;
    }

    &.iconButton {
      width: 36px;
      height: 36px;
      min-height: 36px;
      padding: 10px;
    }

    .children {
      transform: none;
    }

    @include for-phone-only {
      height: 52px;
      min-height: 52px;
      border-radius: 12px;

      &.iconButton {
        width: 52px;
        height: 52px;
        min-height: 52px;
      }
    }
  }

  &.iconButton {
    padding: 10px 12px;
    width: 40px;
    height: 40px;
  }

  &.square {
    padding: 10px;
    min-height: 40px;
    min-width: 40px;
  }

  .children {
    padding: 0 4px;
    transform: translateY(-1px);
  }
}

.xl {
  @include md-sohne-medium;
  line-height: 20px;
  gap: 2px;
  padding: 12px 15px;
  border-radius: 10px;
  min-height: 48px;

  [data-autopilot-system-redesign='on'] & {
    @include md-sohne;
    padding: 12px;
    border-radius: 10px;
    height: 48px;
    min-height: 48px;
    line-height: 125%; /* 20px */

    &.vertical {
      min-height: 48px;
    }

    &.iconButton {
      width: 48px;
      height: 48px;
      min-height: 48px;
      padding: 12px;
    }

    .children {
      transform: none;
    }

    @include for-phone-only {
      height: 60px;
      min-height: 60px;

      &.iconButton {
        width: 60px;
        height: 60px;
        min-height: 60px;
      }
    }
  }

  &.iconButton {
    padding: 12px 15px;
    width: 48px;
    height: 48px;
  }

  &.square {
    padding: 12px;
    min-height: 48px;
    min-width: 48px;
  }

  .children {
    padding: 0 4px;
    transform: translateY(-1px);
  }
}

// Variants
.primary {
  background: var(--dark-80);
  color: var(--light-100);

  [data-autopilot-system-redesign='on'] & {
    background: var(--dark-90);

    &.hovered {
      background: #000;
    }
  }

  .endIcon,
  .frontIcon {
    fill: var(--light-100);
  }

  .chevron {
    fill: var(--light-100);
  }

  &.pressed:after {
    content: '';
    position: absolute;
    width: 100%;
    height: 100%;
    background: var(--light-8);
  }

  &.focused:after {
    content: '';
    position: absolute;
    width: 100%;
    height: 100%;
    background: var(--light-8);
  }

  &.hovered {
    color: var(--light-100);
    background: var(--selected-fill);
  }
}

.primary-colored {
  background: var(--selected-fill);
  color: var(--light-100);

  .endIcon,
  .frontIcon {
    fill: var(--light-100);
  }

  .chevron {
    fill: var(--light-100);
  }

  &.pressed:after {
    content: '';
    position: absolute;
    width: 100%;
    height: 100%;
    background: var(--light-8);
  }

  &.focused:after {
    content: '';
    position: absolute;
    width: 100%;
    height: 100%;
    background: var(--light-8);
  }

  &.hovered {
    opacity: 0.8;
  }
}

.red {
  background: var(--red-50);
  color: var(--light-100);

  .endIcon,
  .frontIcon {
    fill: var(--light-100);
  }

  .chevron {
    fill: var(--light-100);
  }

  &.pressed:after {
    content: '';
    position: absolute;
    width: 100%;
    height: 100%;
    background: var(--light-8);
  }

  &.focused:after {
    content: '';
    position: absolute;
    width: 100%;
    height: 100%;
    background: var(--light-8);
  }

  &.hovered {
    color: var(--light-100);
    background: #d40a14;
  }
}

.secondary {
  background: var(--light-100);
  color: var(--dark-80);
  box-shadow: 0px 2px 3px 0px rgba(0, 0, 0, 0.05);
  border: 1px solid var(--dark-8);

  [data-autopilot-system-redesign='on'] & {
    color: var(--dark-90);
    border: 1px solid var(--dark-8);
    box-shadow: none;

    .endIcon,
    .frontIcon {
      fill: var(--dark-90);
    }

    &.hovered {
      background: var(--light-100);
      border: 1px solid var(--dark-15);
    }

    &.pressed {
      background: var(--light-100);
      border: 1px solid var(--dark-40);
      box-shadow: none;
    }

    &.focused {
      background: var(--light-100);
      box-shadow: none;
      border: none;
      box-shadow: 0 0 0 2px rgba(0, 0, 0, 0.25);
    }
  }

  .endIcon,
  .frontIcon {
    fill: var(--dark-80);
  }

  &.hovered {
    background: var(--dark-4);
  }

  &.focused,
  &.pressed {
    background: var(--dark-8);
    box-shadow: inset 0px 2px 5px 0px rgba(0, 0, 0, 0.07), 0px 0px 4px 0px rgba(0, 0, 0, 0.03);
  }
}

.secondary-bg-agnostic {
  @extend .secondary;

  &.hovered {
    background: linear-gradient(0deg, var(--dark-4) 0%, var(--dark-4) 100%), var(--light-100) !important;
  }
}

@media (min-width: $phoneMaxWidth) {
  .secondary,
  .secondary-bg-agnostic {
    border: 1px solid var(--dark-15);
  }
}

@mixin tertiary-styles {
  background: var(--dark-4);
  color: var(--dark-80);

  .endIcon,
  .frontIcon {
    fill: var(--dark-80);
  }

  &.pressed {
    background: var(--dark-8);
    box-shadow: 1px 1px 3px 0px rgba(0, 0, 0, 0.09) inset;
  }

  &.focused,
  &.hovered {
    color: var(--dark-80);
    background: var(--dark-8);
  }

  /* stylelint-disable css-modules/no-global-scoped-selector */
  [data-autopilot-system-redesign='on'] & {
    background: transparent;
    color: var(--dark-90);

    .endIcon,
    .frontIcon {
      fill: var(--dark-90);
    }

    &.hovered {
      background: var(--dark-3);
    }

    &.pressed {
      background: var(--dark-8);
      box-shadow: none;
    }

    &.focused {
      color: var(--dark-90);
      background: transparent;
      box-shadow: 0 0 0 2px rgba(0, 0, 0, 0.25);
    }
  }
  /* stylelint-enable css-modules/no-global-scoped-selector */
}

.tertiary {
  @include tertiary-styles;
}

.danger {
  @include tertiary-styles;
  color: var(--red-70);
}

.subtle {
  background: var(--dark-40);
  color: var(--light-100);

  &.xs {
    color: var(--dark-60);
  }

  .endIcon,
  .frontIcon {
    fill: var(--dark-80);
  }

  &.hovered {
    background: var(--dark-8);
  }

  &.pressed {
    background: var(--dark-8);
    box-shadow: 1px 1px 3px 0px rgba(0, 0, 0, 0.09) inset;
  }

  &.focused {
    background: var(--dark-4);
  }

  [data-autopilot-system-redesign='on'] & {
    background: transparent;
    color: var(--dark-90);

    .endIcon,
    .frontIcon {
      fill: var(--dark-90);
    }

    &.hovered {
      background: var(--dark-3);
    }

    &.pressed {
      background: var(--dark-8);
      box-shadow: none;
    }

    &.focused {
      color: var(--dark-90);
      background: transparent;
      box-shadow: 0 0 0 2px rgba(0, 0, 0, 0.25);
    }
  }
}

.ghost,
.ghost-tertiary,
a.ghost {
  background: transparent;
  color: var(--selected-fill);

  .endIcon,
  .frontIcon {
    fill: var(--selected-fill);
  }

  &.pressed:after {
    content: '';
    position: absolute;
    width: 100%;
    height: 100%;
    background: var(--light-8);
  }

  &.focused:after {
    content: '';
    position: absolute;
    width: 100%;
    height: 100%;
    background: var(--light-8);
  }

  &.hovered {
    background: var(--default-bg);
  }

  [data-autopilot-system-redesign='on'] & {
    background: transparent;
    color: var(--dark-90);

    .endIcon,
    .frontIcon {
      fill: var(--dark-90);
    }

    &.hovered {
      background: var(--dark-3);
    }

    &.pressed {
      background: var(--dark-8);
      box-shadow: none;
    }

    &.focused {
      color: var(--dark-90);
      background: transparent;
      box-shadow: 0 0 0 2px rgba(0, 0, 0, 0.25);
    }
  }
}

.ghost-tertiary {
  background: var(--selected-fill-10);

  &.hovered {
    background: var(--selected-fill-15);
  }

  [data-autopilot-system-redesign='on'] & {
    background: transparent;
    color: var(--dark-90);

    .endIcon,
    .frontIcon {
      fill: var(--dark-90);
    }

    &.hovered {
      background: var(--dark-3);
    }

    &.pressed {
      background: var(--dark-8);
      box-shadow: none;
    }

    &.focused {
      color: var(--dark-90);
      background: transparent;
      box-shadow: 0 0 0 2px rgba(0, 0, 0, 0.25);
    }
  }
}

.editor {
  color: var(--dark-80);

  .endIcon,
  .frontIcon {
    fill: var(--dark-80);
  }

  &.pressed {
    background: var(--dark-8);
    box-shadow: 1px 1px 3px 0px rgba(0, 0, 0, 0.09) inset;
  }

  &.focused {
    background: var(--dark-4);
    color: var(--dark-80);
  }

  &.hovered {
    background: var(--dark-4);
  }

  [data-autopilot-system-redesign='on'] & {
    background: transparent;
    color: var(--dark-90);

    .endIcon,
    .frontIcon {
      fill: var(--dark-90);
    }

    &.hovered {
      background: var(--dark-3);
    }

    &.pressed {
      background: var(--dark-8);
      box-shadow: none;
    }

    &.focused {
      color: var(--dark-90);
      background: transparent;
      box-shadow: 0 0 0 2px rgba(0, 0, 0, 0.25);
    }
  }
}

.green {
  color: var(--light-100);
  background: var(--green-50);

  .endIcon,
  .frontIcon {
    fill: var(--light-100);
  }

  &.pressed {
    background: var(--green-70);
    box-shadow: 1px 1px 3px 0px rgba(0, 0, 0, 0.09) inset;
  }

  &.focused {
    background: var(--green-70);
    color: var(--light-100);
  }

  &.hovered {
    background: var(--green-70);
  }
}
